// @flow

import type {
  Action,
  Catalog,
  CatalogState,
  CreationCatalog,
  Client,
  DiskPreviewInfo,
  ExclusionInfo,
  FieldDefinition,
  GetState,
  IngestConfig,
  MetadataDefinition,
  MetadataMapping,
  SmartKeywordsSettings,
} from 'state/types';
import {
  ADD_CUSTOM_XMP_NAMESPACES,
  ADD_CUSTOM_XMP_NAMESPACES_FAIL,
  ADD_CUSTOM_XMP_NAMESPACES_SUCCESS,
  ADD_FIELDS,
  ADD_FIELDS_FAIL,
  ADD_FIELDS_SUCCESS,
  ADD_NESTED_VALUES,
  ADD_NESTED_VALUES_FAIL,
  ADD_NESTED_VALUES_SUCCESS,
  ADD_OR_UPDATE_USER_CATALOG_INFO,
  ADD_OR_UPDATE_USER_CATALOG_INFO_FAIL,
  ADD_OR_UPDATE_USER_CATALOG_INFO_SUCCESS,
  CREATE_CATALOG,
  CREATE_CATALOG_FAIL,
  CREATE_CATALOG_SUCCESS,
  CREATE_CATALOG_TEMPLATE,
  CREATE_CATALOG_TEMPLATE_FAIL,
  CREATE_CATALOG_TEMPLATE_SUCCESS,
  CREATE_METADATA_DEFINITIONS,
  CREATE_METADATA_DEFINITIONS_SUCCESS,
  CREATE_METADATA_DEFINITIONS_FAIL,
  CREATE_METADATA_MAPPINGS,
  CREATE_METADATA_MAPPINGS_FAIL,
  CREATE_METADATA_MAPPINGS_SUCCESS,
  DELETE_CATALOG,
  DELETE_CATALOG_FAIL,
  DELETE_CATALOG_SUCCESS,
  DELETE_CATALOG_TEMPLATE,
  DELETE_CATALOG_TEMPLATE_FAIL,
  DELETE_CATALOG_TEMPLATE_SUCCESS,
  DELETE_METADATA_DEFINITION,
  DELETE_METADATA_DEFINITION_FAIL,
  DELETE_METADATA_DEFINITION_SUCCESS,
  DELETE_METADATA_MAPPINGS,
  DELETE_METADATA_MAPPINGS_FAIL,
  DELETE_METADATA_MAPPINGS_SUCCESS,
  DELETE_NESTED_VALUES,
  DELETE_NESTED_VALUES_FAIL,
  DELETE_NESTED_VALUES_SUCCESS,
  GET_CATALOGS,
  GET_CATALOGS_FAIL,
  GET_CATALOGS_SUCCESS,
  GET_CATALOG_PROPERTIES,
  GET_CATALOG_PROPERTIES_FAIL,
  GET_CATALOG_PROPERTIES_SUCCESS,
  GET_CATALOG_TYPES,
  GET_CATALOG_TYPES_FAIL,
  GET_CATALOG_TYPES_SUCCESS,
  GET_CUSTOM_XMP_NAMESPACES,
  GET_CUSTOM_XMP_NAMESPACES_FAIL,
  GET_CUSTOM_XMP_NAMESPACES_SUCCESS,
  GET_FIELDS,
  GET_FIELDS_FAIL,
  GET_FIELDS_SUCCESS,
  GET_METADATA,
  GET_METADATA_FAIL,
  GET_METADATA_SUCCESS,
  GET_METADATA_MAPPINGS,
  GET_METADATA_MAPPINGS_FAIL,
  GET_METADATA_MAPPINGS_SUCCESS,
  GET_USER_CATALOG_INFO,
  GET_USER_CATALOG_INFO_FAIL,
  GET_USER_CATALOG_INFO_SUCCESS,
  REMOVE_ALL_PREDEFINED_VALUES,
  REMOVE_ALL_PREDEFINED_VALUES_FAIL,
  REMOVE_ALL_PREDEFINED_VALUES_SUCCESS,
  REMOVE_CUSTOM_XMP_NAMESPACES,
  REMOVE_CUSTOM_XMP_NAMESPACES_FAIL,
  REMOVE_CUSTOM_XMP_NAMESPACES_SUCCESS,
  REMOVE_FIELDS,
  REMOVE_FIELDS_FAIL,
  REMOVE_FIELDS_SUCCESS,
  REMOVE_USERS_FROM_CATALOG,
  REMOVE_USERS_FROM_CATALOG_FAIL,
  REMOVE_USERS_FROM_CATALOG_SUCCESS,
  RENAME_CATALOG,
  RENAME_CATALOG_SUCCESS,
  RENAME_CATALOG_FAIL,
  RENAME_CATALOG_TEMPLATE,
  RENAME_CATALOG_TEMPLATE_FAIL,
  RENAME_CATALOG_TEMPLATE_SUCCESS,
  RESTORE_FILE_TYPES_DEFAULT,
  RESTORE_FILE_TYPES_DEFAULT_SUCCESS,
  RESTORE_FILE_TYPES_DEFAULT_FAIL,
  SET_EXCLUSION_INFO,
  SET_EXCLUSION_INFO_SUCCESS,
  SET_EXCLUSION_INFO_FAIL,
  SET_INGEST_CONFIG,
  SET_INGEST_CONFIG_FAIL,
  SET_INGEST_CONFIG_SUCCESS,
  SET_SELECTED_FIELD_CATEGORY,
  SET_SELECTED_FIELDS_LIST_ITEM_NAME,
  UPDATE_CATALOGING_OPTIONS,
  UPDATE_CATALOGING_OPTIONS_FAIL,
  UPDATE_CATALOGING_OPTIONS_SUCCESS,
  UPDATE_DISK_PREVIEW_INFO,
  UPDATE_DISK_PREVIEW_INFO_FAIL,
  UPDATE_DISK_PREVIEW_INFO_SUCCESS,
  UPDATE_FIELDS,
  UPDATE_FIELDS_FAIL,
  UPDATE_FIELDS_SUCCESS,
  UPDATE_PREDEFINED_LIST,
  UPDATE_PREDEFINED_LIST_FAIL,
  UPDATE_PREDEFINED_LIST_SUCCESS,
  UPDATE_SMART_KEYWORDS_SETTINGS,
  UPDATE_SMART_KEYWORDS_SETTINGS_FAIL,
  UPDATE_SMART_KEYWORDS_SETTINGS_SUCCESS,
} from 'state/types/catalogTypes';
import logger from 'portfolio-shared/util/logging';
import { CATALOG_STORAGE_TYPE, API } from 'util/constants';
import type {
  CatalogingOptions,
  NamespaceDefinition,
  UserCatalogInfo,
} from 'state/types/catalogTypes';

const initialState: CatalogState = {
  catalogs: [], // global
  catalogTypes: [], // global
  catalogProperties: {}, // for selected catalog
  customXMPNamespaces: [], // global
  fields: [], // for selected catalog
  metadataDefinitions: [], // global
  metadataMappings: [], // global
  selectedFieldCategory: 'All', // global
  selectedFieldsListItemName: null, // for selected catalog
  userCatalogInfo: [], // for selected catalog
};

export default function reducer(
  state: CatalogState = initialState,
  action: Action
) {
  switch (action.type) {
    case GET_CATALOGS_SUCCESS:
      return {
        ...state,
        catalogs: (action.payload.sort((a, b) =>
          a.name.localeCompare(b.name)
        ): Catalog[]),
      };
    case GET_CATALOG_PROPERTIES_SUCCESS:
      return {
        ...state,
        catalogProperties: action.payload,
      };
    case GET_CATALOG_PROPERTIES_FAIL:
      return {
        ...state,
        catalogProperties: {},
      };
    case GET_CATALOG_TYPES_SUCCESS:
      return {
        ...state,
        catalogTypes: action.payload,
      };
    case GET_CATALOG_TYPES_FAIL:
      return {
        ...state,
        catalogTypes: [],
      };
    case GET_CUSTOM_XMP_NAMESPACES_SUCCESS:
      return {
        ...state,
        customXMPNamespaces: action.payload,
      };
    case GET_FIELDS_SUCCESS:
      return {
        ...state,
        fields: action.payload,
      };
    case GET_FIELDS_FAIL:
      return {
        ...state,
        fields: [],
      };
    case GET_METADATA_SUCCESS:
      return {
        ...state,
        metadataDefinitions: (action.payload.sort((a, b) =>
          a.displayName.localeCompare(b.displayName)
        ): MetadataDefinition[]),
      };
    case GET_METADATA_FAIL:
      return {
        ...state,
        metadataDefinitions: [],
      };
    case GET_METADATA_MAPPINGS_SUCCESS:
      return {
        ...state,
        metadataMappings: action.payload,
      };
    case GET_METADATA_MAPPINGS_FAIL:
      return {
        ...state,
        metadataMappings: [],
      };
    case GET_USER_CATALOG_INFO_SUCCESS:
      return {
        ...state,
        userCatalogInfo: action.payload,
      };
    case SET_SELECTED_FIELD_CATEGORY:
      return {
        ...state,
        selectedFieldCategory: action.payload,
      };
    case SET_SELECTED_FIELDS_LIST_ITEM_NAME:
      return {
        ...state,
        selectedFieldsListItemName: action.payload,
      };
    default:
      return state;
  }
}

function getCatalogs(identitySession: string) {
  return {
    types: [GET_CATALOGS, GET_CATALOGS_SUCCESS, GET_CATALOGS_FAIL],
    promise: (client: Client, getState: GetState) =>
      client.get(`${API}/catalog`, {
        params: { session: identitySession },
      }),
  };
}

function getCatalogProperties(
  identitySession: string,
  catalogId: string,
  properties: string[]
) {
  return {
    types: [
      GET_CATALOG_PROPERTIES,
      GET_CATALOG_PROPERTIES_SUCCESS,
      GET_CATALOG_PROPERTIES_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.get(
        `${API}/catalog/${catalogId}/properties/${properties.join()}`,
        {
          params: { session: identitySession },
        }
      ),
  };
}

function getCatalogTypes(identitySession: string, locale: string) {
  return {
    types: [
      GET_CATALOG_TYPES,
      GET_CATALOG_TYPES_SUCCESS,
      GET_CATALOG_TYPES_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.get(`${API}/catalog-template`, {
        params: { session: identitySession, locale: locale },
      }),
  };
}

function createCatalogTemplate(
  identitySession: string,
  catalogId: string,
  templateName: string
) {
  return {
    types: [
      CREATE_CATALOG_TEMPLATE,
      CREATE_CATALOG_TEMPLATE_SUCCESS,
      CREATE_CATALOG_TEMPLATE_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.post(`${API}/catalog-template`, {
        params: {
          session: identitySession,
          catalog_id: catalogId,
          name: templateName,
        },
      }),
  };
}

function createCatalog(
  adminSession: string,
  catalog: CreationCatalog,
  createWatchFolder: boolean,
  watchFolderNamePartFiles: string
) {
  logger.debug('Create catalog: ', catalog);
  return {
    types: [CREATE_CATALOG, CREATE_CATALOG_SUCCESS, CREATE_CATALOG_FAIL],
    promise: (client: Client, getState: GetState) =>
      client.post(`${API}/catalog`, {
        data: catalog,
        params: {
          session: adminSession,
          addAdminUser: true,
          createDiskPreviewSettings: true,
          createWatchFolder:
            catalog.storageType &&
            catalog.storageType === CATALOG_STORAGE_TYPE.FILESYSTEM &&
            createWatchFolder,
          watchFolderNamePartFiles,
        },
      }),
  };
}

function deleteCatalog(identitySession: string, catalog: Catalog) {
  logger.debug('Delete catalog: ', catalog);
  return {
    types: [DELETE_CATALOG, DELETE_CATALOG_SUCCESS, DELETE_CATALOG_FAIL],
    promise: (client: Client, getState: GetState) =>
      client.delete(`${API}/catalog/${catalog.name}`, {
        params: {
          session: identitySession,
        },
      }),
  };
}

function deleteCatalogTemplate(identitySession: string, templateGuid: string) {
  return {
    types: [
      DELETE_CATALOG_TEMPLATE,
      DELETE_CATALOG_TEMPLATE_SUCCESS,
      DELETE_CATALOG_TEMPLATE_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.delete(`${API}/catalog-template/${templateGuid}`, {
        params: {
          session: identitySession,
        },
      }),
  };
}

function renameCatalogTemplate(
  identitySession: string,
  templateGuid: string,
  name: string
) {
  return {
    types: [
      RENAME_CATALOG_TEMPLATE,
      RENAME_CATALOG_TEMPLATE_SUCCESS,
      RENAME_CATALOG_TEMPLATE_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.post(`${API}/catalog-template/rename/${templateGuid}`, {
        params: {
          newName: name,
          session: identitySession,
        },
      }),
  };
}

function renameCatalog(identitySession: string, catalog: Catalog) {
  logger.debug('Rename catalog: ', catalog);
  return {
    types: [RENAME_CATALOG, RENAME_CATALOG_SUCCESS, RENAME_CATALOG_FAIL],
    promise: (client: Client, getState: GetState) =>
      client.put(`${API}/catalog/${catalog.catalogId}`, {
        data: catalog,
        params: {
          session: identitySession,
        },
      }),
  };
}

function updateDiskPreviewInfo(
  identitySession: string,
  catalogId: string,
  dpi: DiskPreviewInfo
) {
  return {
    types: [
      UPDATE_DISK_PREVIEW_INFO,
      UPDATE_DISK_PREVIEW_INFO_SUCCESS,
      UPDATE_DISK_PREVIEW_INFO_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.put(`${API}/catalog/${catalogId}/disk-preview-info`, {
        data: dpi,
        params: {
          session: identitySession,
        },
      }),
  };
}

function getFields(identitySession: string, catalogId: string) {
  return {
    types: [GET_FIELDS, GET_FIELDS_SUCCESS, GET_FIELDS_FAIL],
    promise: (client: Client, getState: GetState) =>
      client.get(`${API}/catalog/${catalogId}/field-definition`, {
        params: {
          session: identitySession,
          predefined_values: true,
        },
      }),
  };
}

function addFields(
  identitySession: string,
  catalogId: string,
  fields: FieldDefinition[]
) {
  return {
    types: [ADD_FIELDS, ADD_FIELDS_SUCCESS, ADD_FIELDS_FAIL],
    promise: (client: Client, getState: GetState) =>
      client.post(`${API}/catalog/${catalogId}/field-definition`, {
        data: fields,
        params: {
          session: identitySession,
        },
      }),
  };
}

function updateFields(
  identitySession: string,
  catalogId: string,
  fields: FieldDefinition[]
) {
  return {
    types: [UPDATE_FIELDS, UPDATE_FIELDS_SUCCESS, UPDATE_FIELDS_FAIL],
    promise: (client: Client, getState: GetState) =>
      client.put(`${API}/catalog/${catalogId}/field-definition`, {
        data: fields,
        params: {
          session: identitySession,
        },
      }),
  };
}

function removeFields(
  identitySession: string,
  catalogId: string,
  fieldIds: string[]
) {
  return {
    types: [REMOVE_FIELDS, REMOVE_FIELDS_SUCCESS, REMOVE_FIELDS_FAIL],
    promise: (client: Client, getState: GetState) =>
      client.delete(
        `${API}/catalog/${catalogId}/field-definition/${fieldIds.join()}`,
        {
          params: {
            session: identitySession,
          },
        }
      ),
  };
}

function removeAllPredefinedValues(
  identitySession: string,
  catalogId: string,
  fieldIds: string[]
) {
  return {
    types: [
      REMOVE_ALL_PREDEFINED_VALUES,
      REMOVE_ALL_PREDEFINED_VALUES_SUCCESS,
      REMOVE_ALL_PREDEFINED_VALUES_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.delete(
        `${API}/catalog/${catalogId}/field-definition/predefined-values/${fieldIds.join()}`,
        {
          params: {
            session: identitySession,
          },
        }
      ),
  };
}

function createMetadataDefinitions(
  identitySession: string,
  metadataDefinitions: MetadataDefinition[]
) {
  return {
    types: [
      CREATE_METADATA_DEFINITIONS,
      CREATE_METADATA_DEFINITIONS_SUCCESS,
      CREATE_METADATA_DEFINITIONS_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.put(`${API}/catalog/metadata-definition`, {
        params: {
          session: identitySession,
        },
        data: metadataDefinitions,
      }),
  };
}

function deleteMetadataDefinition(
  identitySession: string,
  metadataDefinitionId: string
) {
  return {
    types: [
      DELETE_METADATA_DEFINITION,
      DELETE_METADATA_DEFINITION_SUCCESS,
      DELETE_METADATA_DEFINITION_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.delete(
        `${API}/catalog/metadata-definition/${metadataDefinitionId}`,
        {
          params: {
            session: identitySession,
          },
        }
      ),
  };
}

function getMetadataDefinitions(identitySession: string) {
  return {
    types: [GET_METADATA, GET_METADATA_SUCCESS, GET_METADATA_FAIL],
    promise: (client: Client, getState: GetState) =>
      client.get(`${API}/catalog/metadata-definition`, {
        params: {
          session: identitySession,
        },
      }),
  };
}

function getMetadataMappings(identitySession: string, catalogId: string) {
  return {
    types: [
      GET_METADATA_MAPPINGS,
      GET_METADATA_MAPPINGS_SUCCESS,
      GET_METADATA_MAPPINGS_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.get(`${API}/catalog/${catalogId}/metadata-mapping`, {
        params: {
          session: identitySession,
        },
      }),
  };
}

function createMetadataMappings(
  identitySession: string,
  catalogId: string,
  mappings: MetadataMapping[]
) {
  return {
    types: [
      CREATE_METADATA_MAPPINGS,
      CREATE_METADATA_MAPPINGS_SUCCESS,
      CREATE_METADATA_MAPPINGS_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.post(`${API}/catalog/${catalogId}/metadata-mapping`, {
        data: mappings,
        params: {
          session: identitySession,
        },
      }),
  };
}

function deleteMetadataMappings(
  identitySession: string,
  catalogId: string,
  mappings: MetadataMapping[]
) {
  return {
    types: [
      DELETE_METADATA_MAPPINGS,
      DELETE_METADATA_MAPPINGS_SUCCESS,
      DELETE_METADATA_MAPPINGS_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.delete(`${API}/catalog/${catalogId}/metadata-mapping`, {
        data: mappings,
        params: {
          session: identitySession,
        },
      }),
  };
}

function setSelectedFieldCategory(category: string) {
  return {
    type: SET_SELECTED_FIELD_CATEGORY,
    payload: category,
  };
}

function setSelectedFieldsListItemName(name: ?string) {
  return {
    type: SET_SELECTED_FIELDS_LIST_ITEM_NAME,
    payload: name,
  };
}

function getUserCatalogInfo(identitySession: string, catalogName: string) {
  return {
    types: [
      GET_USER_CATALOG_INFO,
      GET_USER_CATALOG_INFO_SUCCESS,
      GET_USER_CATALOG_INFO_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.get(`${API}/catalog/membership`, {
        params: {
          session: identitySession,
          catalog_names: catalogName,
        },
      }),
  };
}

function addOrUpdateUserCatalogInfo(
  identitySession: string,
  infos: UserCatalogInfo[]
) {
  return {
    types: [
      ADD_OR_UPDATE_USER_CATALOG_INFO,
      ADD_OR_UPDATE_USER_CATALOG_INFO_SUCCESS,
      ADD_OR_UPDATE_USER_CATALOG_INFO_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.post(`${API}/catalog/membership`, {
        data: infos,
        params: {
          session: identitySession,
        },
      }),
  };
}

function removeUsersFromCatalog(
  identitySession: string,
  catalogName: string,
  userNames: string[]
) {
  return {
    types: [
      REMOVE_USERS_FROM_CATALOG,
      REMOVE_USERS_FROM_CATALOG_SUCCESS,
      REMOVE_USERS_FROM_CATALOG_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.delete(`${API}/catalog/${catalogName}/membership`, {
        data: userNames,
        params: {
          session: identitySession,
        },
      }),
  };
}

function updateCatalogingOptions(
  identitySession: string,
  catalogId: string,
  catOptions: CatalogingOptions
) {
  return {
    types: [
      UPDATE_CATALOGING_OPTIONS,
      UPDATE_CATALOGING_OPTIONS_SUCCESS,
      UPDATE_CATALOGING_OPTIONS_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.put(`${API}/catalog/${catalogId}/cataloging-options`, {
        data: catOptions,
        params: {
          session: identitySession,
        },
      }),
  };
}

function setExclusionInfo(
  identitySession: string,
  catalogId: string,
  exclusionInfo: ExclusionInfo
) {
  return {
    types: [
      SET_EXCLUSION_INFO,
      SET_EXCLUSION_INFO_SUCCESS,
      SET_EXCLUSION_INFO_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.put(`${API}/catalog/${catalogId}/exclusion-info`, {
        data: exclusionInfo,
        params: {
          session: identitySession,
        },
      }),
  };
}

function restoreFileTypesDefault(identitySession: string, catalogId: string) {
  return {
    types: [
      RESTORE_FILE_TYPES_DEFAULT,
      RESTORE_FILE_TYPES_DEFAULT_SUCCESS,
      RESTORE_FILE_TYPES_DEFAULT_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.post(`${API}/catalog/${catalogId}/fileTypes/_restoreDefaults`, {
        params: {
          session: identitySession,
        },
      }),
  };
}

function setIngestConfig(
  identitySession: string,
  catalogId: string,
  ingestConfig: IngestConfig
) {
  return {
    types: [
      SET_INGEST_CONFIG,
      SET_INGEST_CONFIG_SUCCESS,
      SET_INGEST_CONFIG_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.post(`${API}/catalog/${catalogId}/ingest-config`, {
        data: ingestConfig,
        params: {
          session: identitySession,
        },
      }),
  };
}

function updateSmartKeywordsSettings(
  identitySession: string,
  catalogId: string,
  smartKeywordsSettings: SmartKeywordsSettings
) {
  return {
    types: [
      UPDATE_SMART_KEYWORDS_SETTINGS,
      UPDATE_SMART_KEYWORDS_SETTINGS_SUCCESS,
      UPDATE_SMART_KEYWORDS_SETTINGS_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.post(`${API}/catalog/${catalogId}/smart-keywords-settings`, {
        data: smartKeywordsSettings,
        params: {
          session: identitySession,
        },
      }),
  };
}

function addCustomXmpNamespaces(
  identitySession: string,
  namespaces: NamespaceDefinition[]
) {
  return {
    types: [
      ADD_CUSTOM_XMP_NAMESPACES,
      ADD_CUSTOM_XMP_NAMESPACES_SUCCESS,
      ADD_CUSTOM_XMP_NAMESPACES_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.post(`${API}/namespace`, {
        data: namespaces,
        params: {
          session: identitySession,
        },
      }),
  };
}

function getCustomXmpNamespaces(identitySession: string) {
  return {
    types: [
      GET_CUSTOM_XMP_NAMESPACES,
      GET_CUSTOM_XMP_NAMESPACES_SUCCESS,
      GET_CUSTOM_XMP_NAMESPACES_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.get(`${API}/namespace/_custom`, {
        params: {
          session: identitySession,
        },
      }),
  };
}

function removeCustomXmpNamespaces(
  identitySession: string,
  namespaceIds: string[]
) {
  return {
    types: [
      REMOVE_CUSTOM_XMP_NAMESPACES,
      REMOVE_CUSTOM_XMP_NAMESPACES_SUCCESS,
      REMOVE_CUSTOM_XMP_NAMESPACES_FAIL,
    ],
    promise: (client: Client, getState: GetState) =>
      client.delete(`${API}/namespace`, {
        params: {
          session: identitySession,
          nsIds: namespaceIds,
        },
      }),
  };
}

function updatePredefinedList(
  identitySession: string,
  catalogId: string,
  fieldDefId: string,
  valuesToAdd?: string[],
  valuesToRemove?: string[]
) {
  const data = {};
  if (valuesToAdd) {
    data.addPredefinedValues = valuesToAdd;
  }
  if (valuesToRemove) {
    data.removePredefinedValues = valuesToRemove;
  }
  return {
    types: [
      UPDATE_PREDEFINED_LIST,
      UPDATE_PREDEFINED_LIST_SUCCESS,
      UPDATE_PREDEFINED_LIST_FAIL,
    ],
    meta: { catalogId },
    promise: (client: Client, getState: GetState) =>
      client.post(
        `${API}/catalog/${catalogId}/field-definition/${fieldDefId}/predefined-value/`,
        {
          params: {
            session: identitySession,
          },
          data,
        }
      ),
  };
}

function addNestedValues(
  identitySession: string,
  catalogId: string,
  fieldDefId: string,
  valuesToAdd: string[],
  parentNestedValueId?: string
) {
  return {
    types: [
      ADD_NESTED_VALUES,
      ADD_NESTED_VALUES_SUCCESS,
      ADD_NESTED_VALUES_FAIL,
    ],
    meta: { catalogId },
    promise: (client: Client, getState: GetState) =>
      client.post(
        `${API}/catalog/${catalogId}/field-definition/${fieldDefId}/nested-value`,
        {
          params: {
            session: identitySession,
            parentId: parentNestedValueId,
          },
          data: valuesToAdd,
        }
      ),
  };
}

function deleteNestedValues(
  identitySession: string,
  catalogId: string,
  fieldDefId: string,
  idsToRemove: string[]
) {
  return {
    types: [
      DELETE_NESTED_VALUES,
      DELETE_NESTED_VALUES_SUCCESS,
      DELETE_NESTED_VALUES_FAIL,
    ],
    meta: { catalogId, idsToRemove },
    promise: (client: Client, getState: GetState) =>
      client.delete(
        `${API}/catalog/${catalogId}/field-definition/${fieldDefId}/nested-value`,
        {
          params: {
            session: identitySession,
          },
          data: idsToRemove,
        }
      ),
  };
}

const actions = {
  addCustomXmpNamespaces,
  addFields,
  addNestedValues,
  addOrUpdateUserCatalogInfo,
  createCatalog,
  createCatalogTemplate,
  createMetadataDefinitions,
  createMetadataMappings,
  deleteMetadataDefinition,
  deleteCatalog,
  deleteCatalogTemplate,
  deleteMetadataMappings,
  deleteNestedValues,
  getCatalogs,
  getCatalogProperties,
  getCatalogTypes,
  getCustomXmpNamespaces,
  getFields,
  getMetadataDefinitions,
  getMetadataMappings,
  getUserCatalogInfo,
  removeAllPredefinedValues,
  removeCustomXmpNamespaces,
  removeFields,
  removeUsersFromCatalog,
  renameCatalog,
  renameCatalogTemplate,
  restoreFileTypesDefault,
  setExclusionInfo,
  setIngestConfig,
  setSelectedFieldCategory,
  setSelectedFieldsListItemName,
  updateCatalogingOptions,
  updateDiskPreviewInfo,
  updateFields,
  updateSmartKeywordsSettings,
  updatePredefinedList,
};

export { actions };



// WEBPACK FOOTER //
// ./src/state/reducers/catalogReducer.js